import React, { useId } from "react";
import { cn } from "@/lib/utils";
import { NATIONALITIES } from "@/lib/nationalities";

/**
 * Nationality picker — native `<input list="…">` + `<datalist>` (Feb 2026).
 *
 * WHY NATIVE (iPad Safari crash Iter-298): the previous cmdk Popover build
 * white-screened on iPad Safari the moment a guest tapped a nationality —
 * even with items wrapped in a `CommandGroup`, cmdk v1 still tripped over
 * a virtualized-list keyboard path on iOS WebKit and threw
 * `Object.keys(undefined)`. Reverting to the browser-native combobox
 * eliminates that failure surface entirely: no third-party JS runs when
 * the guest picks a row.
 *
 * UX:
 *   • Type-ahead: the browser filters `<option>` values as the guest types.
 *   • Keeps the same 48 px tall, 16 px text tap target so it stays iPad-ergonomic.
 *   • Strict validation lives in the PARENT (`NATIONALITIES.includes(value)`)
 *     — this component intentionally accepts any string so a stale legacy
 *     free-text value renders instead of being silently blanked. The parent's
 *     `canStep1` guard is what stops a typo submission.
 *
 * Shape is unchanged so it's a drop-in replacement for the old cmdk build.
 */
export default function NationalityCombobox({ value, onChange, placeholder = "Type to search nationality…", testId = "nationality-combobox" }) {
  const listId = useId();
  return (
    <>
      <input
        type="text"
        role="combobox"
        aria-autocomplete="list"
        list={listId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="words"
        spellCheck={false}
        className={cn(
          "flex h-12 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background",
          "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        data-testid={testId}
      />
      <datalist id={listId} data-testid={`${testId}-list`}>
        {NATIONALITIES.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
    </>
  );
}
